// Wiederverwendbare Bausteine der Einträge-Liste:
//  - <BulkActions/>  : floating Bar mit Auswahl-Anzahl + Bulk-Delete
//  - <EntryListItem/>: einzelne Zeile mit Checkbox + Edit/Delete
//  - formatEntryDate : Datums-Heading-Formatter (z.B. „Montag, 03. Juni 2026")
//
// Wurden aus apps/web/src/routes/entries.tsx extrahiert, als jene Standalone-
// Seite zugunsten von Kalender ▸ Liste-Ansicht / CalendarInfoPanel aufgelöst
// wurde. Konsumenten: auswertung.tsx (embedded und standalone-deprecated).
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Pencil, Trash2 } from 'lucide-react'
import { api, type TimeEntry } from '../lib/api'
import { invalidateEntryData } from '../lib/invalidate'
import { notify } from '../lib/notify'
import { cn, formatDuration, formatHM, formatTime } from '../lib/utils'
import { Button } from './ui/button'
import { confirm } from './ui/confirm-dialog'

/**
 * Tag-Chips eines Eintrags: voller Tag-Name (truncated bei langen Namen),
 * title/aria-Label, ab >4 Tags kompaktes „+N".
 */
function EntryTagChips({ tags }: { tags: { name: string; color: string }[] }) {
  const visible = tags.slice(0, 4)
  const rest = tags.slice(4)
  return (
    <span className="flex gap-1">
      {visible.map((t) => (
        <span
          key={t.name}
          title={t.name}
          className="inline-block max-w-[6rem] truncate rounded-full px-1.5 py-0.5 text-2xs"
          style={{ backgroundColor: `${t.color}20`, color: t.color }}
        >
          {t.name}
        </span>
      ))}
      {rest.length > 0 && (
        <span
          title={rest.map((t) => t.name).join(', ')}
          className="inline-block rounded-full bg-bg-overlay px-1.5 py-0.5 text-2xs text-text-tertiary"
        >
          +{rest.length}
        </span>
      )}
    </span>
  )
}

/**
 * Dezenter Trenner zwischen zwei Einträgen, der die zeitliche Pause/Lücke
 * anzeigt (z.B. „Pause 0:45"). Nicht interaktiv. Wird nur ab MIN_GAP_MINUTES
 * gerendert (siehe lib/gaps.ts).
 */
export function BreakRow({ minutes }: { minutes: number }) {
  return (
    <li className="flex items-center justify-center gap-2 px-3 py-1 text-text-tertiary">
      <span className="h-px flex-1 bg-border-subtle/60" aria-hidden />
      <span className="font-mono text-2xs uppercase tracking-wider tabular-nums">
        Pause {formatHM(minutes)}
      </span>
      <span className="h-px flex-1 bg-border-subtle/60" aria-hidden />
    </li>
  )
}

export function BulkActions({
  selectedIds,
  onCleared,
}: {
  selectedIds: string[]
  onCleared: () => void
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.bulkDeleteEntries(selectedIds),
    onSuccess: ({ deleted }) => {
      invalidateEntryData(queryClient)
      notify.success(`${deleted} Einträge gelöscht`)
      onCleared()
    },
    onError: (err: Error) => notify.error(err.message),
  })

  return (
    <div className="sticky top-2 z-10 flex items-center justify-between gap-3 rounded-2xl border border-accent/30 bg-bg-elevated/90 backdrop-blur-md px-4 py-3 shadow-lg">
      <p className="text-sm">
        <span className="font-medium text-accent">{selectedIds.length}</span> Einträge ausgewählt
      </p>
      <div className="flex items-center gap-2">
        <Button variant="ghost" size="sm" onClick={onCleared}>
          Auswahl aufheben
        </Button>
        <Button
          variant="danger"
          size="sm"
          disabled={remove.isPending}
          onClick={async () => {
            const ok = await confirm({
              title: `${selectedIds.length} Einträge löschen?`,
              variant: 'danger',
              confirmLabel: 'Löschen',
            })
            if (ok) remove.mutate()
          }}
        >
          <Trash2 className="h-4 w-4" /> Löschen
        </Button>
      </div>
    </div>
  )
}

export function EntryListItem({
  entry,
  project,
  customer,
  tagMap,
  isLast,
  selected,
  onSelectToggle,
  onEdit,
  compact = false,
  readOnly = false,
}: {
  entry: TimeEntry
  project: { name: string } | null
  customer: { name: string } | null
  tagMap: Map<string, { name: string; color: string }>
  isLast: boolean
  selected: boolean
  onSelectToggle: () => void
  onEdit: () => void
  /** Schmälere Darstellung für den CalendarInfoPanel-Embed
   *  (~380px). Checkbox + Edit + Delete + Tags bleiben drin —
   *  nur Time-Spalte wandert in die zweite Zeile, Padding ist
   *  geringer. */
  compact?: boolean
  /** Lesemodus — blendet Auswahl, Bearbeiten und Löschen aus (z.B. wenn ein
   *  Admin im Kalender die Zeiten eines anderen Users nur ANSIEHT). */
  readOnly?: boolean
}) {
  const queryClient = useQueryClient()
  const remove = useMutation({
    mutationFn: () => api.deleteEntry(entry.id),
    onSuccess: () => {
      invalidateEntryData(queryClient)
      notify.success('Eintrag gelöscht')
    },
    onError: (err: Error) => notify.error(err.message),
  })
  const start = new Date(entry.startTime)
  const end = entry.endTime ? new Date(entry.endTime) : null
  const duration = (end ? end.getTime() : Date.now()) - start.getTime()
  const isRunning = !end
  const entryTags = (entry.tagIds ?? []).map((id) => tagMap.get(id)).filter(Boolean) as {
    name: string
    color: string
  }[]

  if (compact) {
    // 380px-friendly layout: erste Zeile Checkbox + Description + Duration,
    // zweite Zeile Time-Range + Projekt + Tags + Edit/Delete. Actions
    // sind hier IMMER sichtbar (touch:opacity-100 plus kein hover-Gate)
    // damit der Touch-Workflow auf dem Tablet nicht hinterherhinkt.
    return (
      <li
        className={cn(
          'flex flex-col gap-1 px-3 py-2.5 transition-colors',
          !isLast && 'border-b border-border-subtle',
          isRunning && 'bg-accent/[0.04]',
          selected && 'bg-accent/[0.08]',
        )}
      >
        <div className="flex items-start gap-2">
          {!readOnly && (
            <input
              type="checkbox"
              checked={selected}
              onChange={onSelectToggle}
              className="h-4 w-4 mt-0.5 accent-accent cursor-pointer shrink-0"
              aria-label="Auswählen"
            />
          )}
          {readOnly ? (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-sm text-text-primary truncate">{entry.description}</p>
            </div>
          ) : (
            <button type="button" onClick={onEdit} className="flex-1 min-w-0 text-left">
              <p className="text-sm text-text-primary truncate">{entry.description}</p>
            </button>
          )}
          <p
            className={cn(
              'font-mono tabular-nums text-xs shrink-0 mt-0.5',
              isRunning ? 'text-accent' : 'text-text-secondary',
            )}
          >
            {formatDuration(duration)}
          </p>
        </div>
        <div className="pl-6 flex items-center gap-2 flex-wrap text-2xs text-text-tertiary">
          <span className="font-mono tabular-nums">
            {formatTime(entry.startTime)}–{end ? formatTime(end.toISOString()) : '…'}
          </span>
          {(customer || project) && (
            <>
              <span className="opacity-50">·</span>
              <span className="truncate max-w-[12rem]">
                {customer?.name}
                {customer && project && ' · '}
                {project?.name}
              </span>
            </>
          )}
          {entryTags.length > 0 && <EntryTagChips tags={entryTags} />}
          {!readOnly && (
            <div className="ml-auto flex items-center gap-0.5">
              <button
                type="button"
                onClick={onEdit}
                aria-label="Bearbeiten"
                title="Bearbeiten"
                className="text-text-tertiary hover:text-accent p-1 rounded hover:bg-bg-overlay/60 touch:opacity-100"
              >
                <Pencil className="h-3 w-3" />
              </button>
              <button
                type="button"
                onClick={async () => {
                  const ok = await confirm({
                    title: 'Eintrag löschen?',
                    description: entry.description,
                    variant: 'danger',
                    confirmLabel: 'Löschen',
                  })
                  if (ok) remove.mutate()
                }}
                aria-label="Löschen"
                title="Löschen"
                className="text-text-tertiary hover:text-danger p-1 rounded hover:bg-bg-overlay/60 touch:opacity-100"
              >
                <Trash2 className="h-3 w-3" />
              </button>
            </div>
          )}
        </div>
        {entry.notes && (
          <p className="pl-6 text-2xs italic text-text-tertiary truncate">{entry.notes}</p>
        )}
      </li>
    )
  }

  return (
    <li
      className={`group grid grid-cols-[auto_auto_1fr_auto_auto] items-center gap-4 px-5 py-3.5 transition-colors hover:bg-bg-overlay/40 ${
        !isLast ? 'border-b border-border-subtle' : ''
      } ${isRunning ? 'bg-accent/[0.04]' : ''} ${selected ? 'bg-accent/[0.08]' : ''}`}
    >
      <input
        type="checkbox"
        checked={selected}
        onChange={onSelectToggle}
        className="h-4 w-4 accent-accent cursor-pointer"
        aria-label="Auswählen"
      />
      <div className="font-mono tabular-nums text-xs text-text-tertiary w-28">
        {formatTime(entry.startTime)} – {end ? formatTime(end.toISOString()) : '…'}
      </div>
      <div className="min-w-0">
        <p className="truncate text-sm text-text-primary">{entry.description}</p>
        {(project || customer || entryTags.length > 0) && (
          <p className="mt-0.5 flex items-center gap-2 text-xs text-text-tertiary truncate">
            {customer && <span className="truncate">{customer.name}</span>}
            {customer && project && <span className="shrink-0">·</span>}
            {project && <span className="truncate">{project.name}</span>}
            {entryTags.length > 0 && <EntryTagChips tags={entryTags} />}
          </p>
        )}
        {entry.notes && (
          <p className="mt-1 text-xs italic text-text-tertiary truncate">{entry.notes}</p>
        )}
      </div>
      <p
        className={`font-mono tabular-nums text-sm ${
          isRunning ? 'text-accent' : 'text-text-secondary'
        }`}
      >
        {formatDuration(duration)}
      </p>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 touch:opacity-100 transition-opacity">
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={onEdit}
          aria-label="Bearbeiten"
          className="text-text-tertiary hover:text-accent"
        >
          <Pencil className="h-3.5 w-3.5" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          onClick={async () => {
            const ok = await confirm({
              title: 'Eintrag löschen?',
              description: entry.description,
              variant: 'danger',
              confirmLabel: 'Löschen',
            })
            if (ok) remove.mutate()
          }}
          aria-label="Löschen"
          className="text-text-tertiary hover:text-danger"
        >
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </div>
    </li>
  )
}

export function formatEntryDate(iso: string): string {
  // Reine Datums-Keys (`YYYY-MM-DD`, z.B. aus groupEntriesByLocalDay) als
  // LOKALES Datum parsen — new Date('YYYY-MM-DD') wäre UTC-Mitternacht und
  // würde in Zeitzonen westlich von UTC einen Tag zurückspringen (D8).
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(iso)
  const d = dateOnly
    ? new Date(Number(dateOnly[1]), Number(dateOnly[2]) - 1, Number(dateOnly[3]))
    : new Date(iso)
  return d.toLocaleDateString('de-DE', {
    weekday: 'long',
    day: '2-digit',
    month: 'long',
    year: 'numeric',
  })
}
